import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import useSimulationScores from '../../hooks/useSimulationScores';
import { ATTRIBUTES, SEGMENTS, SEGMENT_NAMES } from '../../scoring';
import { getGameById, updateActiveTeams, updateActiveRounds } from '../../firebase/db';
import AiInsightsPanel from './AiInsightsPanel';
import UnitShareView from './UnitShareView';
import DollarShareView from './DollarShareView';
import ResearchReport from './ResearchReport';
import IndividualTeamResults from './IndividualTeamResults';
import AdvertisingSpendView from './AdvertisingSpendView';
import ExecutiveSummary from './ExecutiveSummary';

const TABS = [
    { key: 'summary', label: 'Executive\nSummary' },
    { key: 'unitShare', label: 'Unit Share\nby Team' },
    { key: 'dollarShare', label: 'Dollar Share\nby Team' },
    { key: 'research', label: 'Research\nReport' },
    { key: 'adSpend', label: 'Advertising\nSpend' },
    { key: 'teamResults', label: 'Individual Team\nResults' },
];

const styles = {
    container: {
        maxWidth: '1100px', margin: '40px auto', padding: '20px',
        boxShadow: '0 4px 12px rgba(0,0,0,0.1)', borderRadius: '10px',
        fontFamily: 'Arial, sans-serif', backgroundColor: '#fff',
        position: 'relative',
    },
    backBtn: {
        padding: '8px 16px', backgroundColor: '#1976d2', color: '#fff',
        border: 'none', borderRadius: '4px', cursor: 'pointer',
        position: 'absolute', top: '20px', left: '20px', zIndex: 1000,
    },
    header: {
        textAlign: 'center', marginBottom: '20px', fontSize: '24px', color: '#333',
    },
    tabRow: {
        display: 'flex', justifyContent: 'center', gap: '0', marginBottom: '30px',
    },
    tab: (active) => ({
        padding: '14px 24px',
        backgroundColor: active ? '#4b2e83' : '#d9d2e9',
        color: active ? '#fff' : '#333',
        border: 'none', cursor: 'pointer',
        fontSize: '14px', fontWeight: 'bold',
        textAlign: 'center', whiteSpace: 'pre-line',
        minWidth: '120px',
        borderRight: '1px solid #fff',
    }),
    loading: {
        textAlign: 'center', padding: '60px', fontSize: '18px', color: '#666',
    },
    error: {
        textAlign: 'center', padding: '40px', color: '#e15759', fontSize: '16px',
    },
    filterToggle: {
        display: 'flex', justifyContent: 'center', alignItems: 'center',
        gap: '12px', marginBottom: '16px', cursor: 'pointer', userSelect: 'none',
    },
    filterPanel: {
        backgroundColor: '#f5f5f5', border: '1px solid #ddd', borderRadius: '8px',
        padding: '16px', marginBottom: '20px',
    },
    filterGrid: {
        display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px 24px',
        marginBottom: '16px',
    },
    filterCheckbox: {
        display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px',
        cursor: 'pointer',
    },
    filterActions: {
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    },
    filterLink: {
        background: 'none', border: 'none', color: '#4b2e83',
        cursor: 'pointer', fontSize: '13px', textDecoration: 'underline',
        padding: '0', marginRight: '12px',
    },
    applyBtn: (disabled) => ({
        padding: '8px 20px', backgroundColor: disabled ? '#ccc' : '#4b2e83',
        color: '#fff', border: 'none', borderRadius: '6px',
        cursor: disabled ? 'not-allowed' : 'pointer', fontWeight: 'bold', fontSize: '13px',
    }),
    filterIndicator: {
        fontSize: '13px', color: '#666', fontStyle: 'italic',
    },
};

const ScoresPage = () => {
    const { roundNumber, gameId } = useParams();
    const navigate = useNavigate();
    const [activeTab, setActiveTab] = useState('summary');
    const { scores, teams, loading, error, computeScores } = useSimulationScores('Master', gameId);

    const allTeamNames = useMemo(() => teams.map(t => t.teamName).sort(), [teams]);

    const [selectedTeams, setSelectedTeams] = useState([]);
    const [filteredScores, setFilteredScores] = useState(null);
    const [filterOpen, setFilterOpen] = useState(false);
    const [filterLoaded, setFilterLoaded] = useState(false);

    const [selectedRounds, setSelectedRounds] = useState([1, 2, 3]);
    const [roundFilterOpen, setRoundFilterOpen] = useState(false);

    // Load saved filters from Firestore on mount
    useEffect(() => {
        if (!gameId) return;
        const loadFilter = async () => {
            try {
                const game = await getGameById(gameId);
                if (game?.activeTeams && game.activeTeams.length > 0) {
                    setSelectedTeams(game.activeTeams);
                }
                if (game?.activeRounds && game.activeRounds.length > 0) {
                    setSelectedRounds(game.activeRounds.sort((a, b) => a - b));
                }
            } catch (err) {
                console.error('Error loading saved filter:', err);
            } finally {
                setFilterLoaded(true);
            }
        };
        loadFilter();
    }, [gameId]);

    // Initialize selectedTeams when teams load AND after saved filter is loaded
    useEffect(() => {
        if (!filterLoaded || allTeamNames.length === 0) return;
        if (selectedTeams.length === 0) {
            setSelectedTeams(allTeamNames);
        } else {
            const validTeams = selectedTeams.filter(name => allTeamNames.includes(name));
            if (validTeams.length === 0) {
                setSelectedTeams(allTeamNames);
            } else {
                setSelectedTeams(validTeams);
                if (validTeams.length < allTeamNames.length) {
                    const filteredTeamList = teams.filter(t => validTeams.includes(t.teamName));
                    setFilteredScores(computeScores(filteredTeamList));
                }
            }
        }
    }, [filterLoaded, allTeamNames]);

    const isFilterActive = selectedTeams.length > 0 && selectedTeams.length < allTeamNames.length;
    const isRoundFilterActive = selectedRounds.length > 0 && selectedRounds.length < 3;

    const toggleRound = (roundNum) => {
        setSelectedRounds(prev =>
            prev.includes(roundNum)
                ? prev.filter(r => r !== roundNum)
                : [...prev, roundNum].sort((a, b) => a - b)
        );
    };

    const handleApplyRoundFilter = async () => {
        if (selectedRounds.length === 0) return;
        try {
            if (selectedRounds.length === 3) {
                await updateActiveRounds(gameId, null);
            } else {
                await updateActiveRounds(gameId, selectedRounds);
            }
        } catch (err) {
            console.error(err);
        }
        setRoundFilterOpen(false);
    };

    const toggleTeam = (teamName) => {
        setSelectedTeams(prev =>
            prev.includes(teamName)
                ? prev.filter(n => n !== teamName)
                : [...prev, teamName]
        );
    };

    const handleApplyFilter = async () => {
        if (selectedTeams.length === 0) return;
        if (selectedTeams.length === allTeamNames.length) {
            setFilteredScores(null);
            try { await updateActiveTeams(gameId, null); } catch (err) { console.error(err); }
        } else {
            const filteredTeamList = teams.filter(t => selectedTeams.includes(t.teamName));
            setFilteredScores(computeScores(filteredTeamList));
            try { await updateActiveTeams(gameId, selectedTeams); } catch (err) { console.error(err); }
        }
        setFilterOpen(false);
    };

    const displayScores = filteredScores || scores;
    const displayTeams = useMemo(
        () => isFilterActive ? teams.filter(t => selectedTeams.includes(t.teamName)) : teams,
        [teams, selectedTeams, isFilterActive]
    );

    const simulationName = 'Cuban Bikes';
    const headerTitle = `${simulationName}: Round ${roundNumber} Scores`;

    const buildGameSummary = useCallback(() => {
        const teamNames = displayTeams.map(t => t.teamName).sort();
        const summary = {
            marketContext: {
                segments: SEGMENT_NAMES.map(name => ({
                    name,
                    population: SEGMENTS[name].population,
                    preferences: SEGMENTS[name].preferences,
                })),
            },
            teams: {},
            activeRounds: selectedRounds,
        };

        teamNames.forEach(name => {
            const team = displayTeams.find(t => t.teamName === name);
            const teamEntry = { rounds: {}, cumulative: { totalProfit: 0, totalUnits: 0, totalRevenue: 0 } };

            selectedRounds.forEach(r => {
                const rs = displayScores[`round${r}`];
                const rd = team?.teamData?.rounds?.[r - 1];
                if (!rs?.[name]) return;

                const result = rs[name];
                const price = parseFloat(rd?.profit_margin?.price_per_bike_dollars) || 0;
                const margin = result.totalRevenue > 0
                    ? ((result.totalProfit / result.totalRevenue) * 100).toFixed(1) + '%' : '0%';

                const totalUnitsInRound = teamNames.reduce((s, n) => s + (rs[n]?.totalUnits || 0), 0);
                const mktShare = totalUnitsInRound > 0
                    ? ((result.totalUnits / totalUnitsInRound) * 100).toFixed(1) + '%' : '0%';

                // Rank in this round
                const profitList = teamNames
                    .filter(n => rs[n])
                    .map(n => ({ n, p: rs[n].totalProfit || 0 }))
                    .sort((a, b) => b.p - a.p);
                const rank = profitList.findIndex(p => p.n === name) + 1;

                const perfTotal = Object.values(rd?.advertising?.performance_ads || {}).reduce((s, v) => s + (v || 0), 0);
                const brandTotal = Object.values(rd?.advertising?.brand_ads || {}).reduce((s, v) => s + (v || 0), 0);

                teamEntry.rounds[r] = {
                    bike: result.bike || {},
                    price, profitMarginPct: margin,
                    adSpend: {
                        total: perfTotal + brandTotal,
                        segments: rd?.advertising?.segments || [],
                        perfTotal, brandTotal,
                    },
                    results: {
                        units: result.totalUnits, revenue: result.totalRevenue,
                        profit: result.totalProfit, marketSharePct: mktShare, rank,
                    },
                };
                teamEntry.cumulative.totalProfit += result.totalProfit || 0;
                teamEntry.cumulative.totalUnits += result.totalUnits || 0;
                teamEntry.cumulative.totalRevenue += result.totalRevenue || 0;
            });

            // Overall rank
            summary.teams[name] = teamEntry;
        });

        // Add overall ranks
        const ranked = Object.entries(summary.teams)
            .sort(([, a], [, b]) => b.cumulative.totalProfit - a.cumulative.totalProfit);
        ranked.forEach(([name], i) => { summary.teams[name].cumulative.overallRank = i + 1; });

        return summary;
    }, [displayScores, displayTeams, selectedRounds]);

    const insightFilterHash = `teams:${isFilterActive ? [...selectedTeams].sort().join(',') : 'all'}|rounds:${selectedRounds.join(',')}`;

    const downloadCsv = useCallback((filename, csvContent) => {
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = filename;
        link.click();
        URL.revokeObjectURL(url);
    }, []);

    const handleExportCsv = useCallback(() => {
        const teamNames = displayTeams.map(t => t.teamName).sort();
        const rounds = selectedRounds;
        let rows = [];

        if (activeTab === 'summary') {
            const header = ['Rank', 'Team', 'Total Profit', 'Total Revenue', 'Total Units', 'Profit Margin %'];
            rows.push(header);
            const teamAgg = {};
            teamNames.forEach(n => {
                teamAgg[n] = { profit: 0, revenue: 0, units: 0 };
                rounds.forEach(r => {
                    const rs = displayScores[`round${r}`];
                    if (rs && rs[n]) {
                        teamAgg[n].profit += rs[n].totalProfit || 0;
                        teamAgg[n].revenue += rs[n].totalRevenue || 0;
                        teamAgg[n].units += rs[n].totalUnits || 0;
                    }
                });
            });
            const ranked = [...teamNames].sort((a, b) => teamAgg[b].profit - teamAgg[a].profit);
            ranked.forEach((n, i) => {
                const a = teamAgg[n];
                const margin = a.revenue > 0 ? ((a.profit / a.revenue) * 100).toFixed(1) : '0.0';
                rows.push([i + 1, n, a.profit.toFixed(2), a.revenue.toFixed(2), a.units, `${margin}%`]);
            });
            downloadCsv(`executive_summary_round${roundNumber}.csv`, rows.map(r => r.join(',')).join('\n'));

        } else if (activeTab === 'unitShare') {
            const header = ['Team', ...rounds.flatMap(r => [`Round ${r} Units`, `Round ${r} %`]), 'Total Units', 'Total %'];
            rows.push(header);
            const roundData = rounds.map(r => {
                const rs = displayScores[`round${r}`];
                if (!rs) return { data: {}, total: 0 };
                let total = 0;
                const data = {};
                teamNames.forEach(n => { data[n] = rs[n]?.totalUnits || 0; total += data[n]; });
                return { data, total };
            });
            let grandTotal = 0;
            const grandTotals = {};
            teamNames.forEach(n => {
                grandTotals[n] = roundData.reduce((s, rd) => s + (rd.data[n] || 0), 0);
                grandTotal += grandTotals[n];
            });
            teamNames.forEach(n => {
                const row = [n];
                roundData.forEach(rd => {
                    row.push(rd.data[n] || 0);
                    row.push(rd.total > 0 ? `${((rd.data[n] / rd.total) * 100).toFixed(1)}%` : '0%');
                });
                row.push(grandTotals[n]);
                row.push(grandTotal > 0 ? `${((grandTotals[n] / grandTotal) * 100).toFixed(1)}%` : '0%');
                rows.push(row);
            });
            downloadCsv(`unit_share_round${roundNumber}.csv`, rows.map(r => r.join(',')).join('\n'));

        } else if (activeTab === 'dollarShare') {
            const header = ['Team', ...rounds.flatMap(r => [`Round ${r} Revenue`, `Round ${r} %`]), 'Total Revenue', 'Total %'];
            rows.push(header);
            const roundData = rounds.map(r => {
                const rs = displayScores[`round${r}`];
                if (!rs) return { data: {}, total: 0 };
                let total = 0;
                const data = {};
                teamNames.forEach(n => { data[n] = rs[n]?.totalRevenue || 0; total += data[n]; });
                return { data, total };
            });
            let grandTotal = 0;
            const grandTotals = {};
            teamNames.forEach(n => {
                grandTotals[n] = roundData.reduce((s, rd) => s + (rd.data[n] || 0), 0);
                grandTotal += grandTotals[n];
            });
            teamNames.forEach(n => {
                const row = [n];
                roundData.forEach(rd => {
                    row.push((rd.data[n] || 0).toFixed(2));
                    row.push(rd.total > 0 ? `${((rd.data[n] / rd.total) * 100).toFixed(1)}%` : '0%');
                });
                row.push(grandTotals[n].toFixed(2));
                row.push(grandTotal > 0 ? `${((grandTotals[n] / grandTotal) * 100).toFixed(1)}%` : '0%');
                rows.push(row);
            });
            downloadCsv(`dollar_share_round${roundNumber}.csv`, rows.map(r => r.join(',')).join('\n'));

        } else if (activeTab === 'research') {
            rounds.forEach(r => {
                const rs = displayScores[`round${r}`];
                if (!rs) return;
                const header = ['Team', ...ATTRIBUTES, 'Price'];
                rows.push([`Round ${r}`]);
                rows.push(header);
                teamNames.forEach(n => {
                    const bike = rs[n]?.bike || {};
                    const teamRaw = displayTeams.find(t => t.teamName === n);
                    const price = parseFloat(teamRaw?.teamData?.rounds?.[r - 1]?.profit_margin?.price_per_bike_dollars) || 0;
                    rows.push([n, ...ATTRIBUTES.map(a => bike[a] ?? ''), price.toFixed(2)]);
                });
                rows.push([]);
            });
            downloadCsv(`research_report_round${roundNumber}.csv`, rows.map(r => r.join(',')).join('\n'));

        } else if (activeTab === 'adSpend') {
            const header = ['Team', ...rounds.flatMap(r => [`R${r} Performance`, `R${r} Brand`, `R${r} Total`]), 'Grand Total', 'Segments Targeted'];
            rows.push(header);
            teamNames.forEach(n => {
                const team = displayTeams.find(t => t.teamName === n);
                const row = [n];
                let grandTotal = 0;
                let latestSegs = [];
                rounds.forEach((r) => {
                    const rd = team?.teamData?.rounds?.[r - 1];
                    if (!rd?.is_submitted) {
                        row.push('', '', '');
                        return;
                    }
                    const perf = Object.values(rd.advertising?.performance_ads || {}).reduce((s, v) => s + (v || 0), 0);
                    const brand = Object.values(rd.advertising?.brand_ads || {}).reduce((s, v) => s + (v || 0), 0);
                    row.push(perf, brand, perf + brand);
                    grandTotal += perf + brand;
                    if (rd.advertising?.segments?.length > 0) latestSegs = rd.advertising.segments;
                });
                row.push(grandTotal, `"${latestSegs.join(', ')}"`);
                rows.push(row);
            });
            downloadCsv(`ad_spend_round${roundNumber}.csv`, rows.map(r => r.join(',')).join('\n'));

        } else if (activeTab === 'teamResults') {
            const header = ['Team', ...rounds.flatMap(r => [`Round ${r} Profit`, `Round ${r} Units`]), 'Total Profit', 'Total Units'];
            rows.push(header);
            teamNames.forEach(n => {
                const row = [n];
                let totalProfit = 0, totalUnits = 0;
                rounds.forEach(r => {
                    const rs = displayScores[`round${r}`];
                    const profit = rs?.[n]?.totalProfit || 0;
                    const units = rs?.[n]?.totalUnits || 0;
                    row.push(profit.toFixed(2), units);
                    totalProfit += profit;
                    totalUnits += units;
                });
                row.push(totalProfit.toFixed(2), totalUnits);
                rows.push(row);
            });

            // Add segment breakdown
            rows.push([]);
            rows.push(['Segment Breakdown']);
            const segHeader = ['Team', 'Segment', ...rounds.map(r => `Round ${r} Units`), 'Total'];
            rows.push(segHeader);
            teamNames.forEach(n => {
                SEGMENT_NAMES.forEach(seg => {
                    const row = [n, seg];
                    let total = 0;
                    rounds.forEach(r => {
                        const units = displayScores[`round${r}`]?.[n]?.segmentResults?.[seg]?.units || 0;
                        row.push(units);
                        total += units;
                    });
                    row.push(total);
                    rows.push(row);
                });
            });
            downloadCsv(`team_results_round${roundNumber}.csv`, rows.map(r => r.join(',')).join('\n'));
        }
    }, [activeTab, displayScores, displayTeams, roundNumber, downloadCsv, selectedRounds]);

    if (loading) {
        return (
            <div style={styles.container}>
                <div style={styles.loading}>Loading scores...</div>
            </div>
        );
    }

    if (error) {
        return (
            <div style={styles.container}>
                <button onClick={() => navigate(-1)} style={styles.backBtn}>Back</button>
                <div style={styles.error}>
                    Error loading scores: {error.message}
                </div>
            </div>
        );
    }

    if (teams.length === 0) {
        return (
            <div style={styles.container}>
                <button onClick={() => navigate(-1)} style={styles.backBtn}>Back</button>
                <h1 style={styles.header}>{headerTitle}</h1>
                <p style={{ textAlign: 'center', color: '#999', fontSize: '16px' }}>
                    No teams have submitted yet.
                </p>
            </div>
        );
    }

    const renderTab = () => {
        switch (activeTab) {
            case 'summary':
                return <ExecutiveSummary scores={displayScores} teams={displayTeams} rounds={selectedRounds} />;
            case 'unitShare':
                return <UnitShareView scores={displayScores} teams={displayTeams} rounds={selectedRounds} />;
            case 'dollarShare':
                return <DollarShareView scores={displayScores} teams={displayTeams} rounds={selectedRounds} />;
            case 'research':
                return <ResearchReport scores={displayScores} teams={displayTeams} rounds={selectedRounds} />;
            case 'adSpend':
                return <AdvertisingSpendView teams={displayTeams} rounds={selectedRounds} />;
            case 'teamResults':
                return <IndividualTeamResults scores={displayScores} teams={displayTeams} rounds={selectedRounds} />;
            default:
                return null;
        }
    };

    return (
        <div style={styles.container}>
            <button onClick={() => navigate(-1)} style={styles.backBtn}>Back</button>

            <h1 style={styles.header}>{headerTitle}</h1>

            {/* Team filter */}
            <div style={styles.filterToggle} onClick={() => setFilterOpen(!filterOpen)}>
                <span style={{ fontWeight: 'bold', fontSize: '14px', color: '#4b2e83' }}>
                    Filter Teams {filterOpen ? '\u25B2' : '\u25BC'}
                </span>
                <span style={styles.filterIndicator}>
                    Showing {isFilterActive ? selectedTeams.length : allTeamNames.length} of {allTeamNames.length} teams
                </span>
            </div>

            {filterOpen && (
                <div style={styles.filterPanel}>
                    <div style={{ marginBottom: '10px' }}>
                        <button
                            style={styles.filterLink}
                            onClick={() => setSelectedTeams([...allTeamNames])}
                        >
                            Select All
                        </button>
                        <button
                            style={styles.filterLink}
                            onClick={() => setSelectedTeams([])}
                        >
                            Deselect All
                        </button>
                    </div>

                    <div style={styles.filterGrid}>
                        {allTeamNames.map(name => (
                            <label key={name} style={styles.filterCheckbox}>
                                <input
                                    type="checkbox"
                                    checked={selectedTeams.includes(name)}
                                    onChange={() => toggleTeam(name)}
                                />
                                {name}
                            </label>
                        ))}
                    </div>

                    <div style={styles.filterActions}>
                        {selectedTeams.length === 0 && (
                            <span style={{ color: '#e15759', fontSize: '13px' }}>
                                Select at least one team
                            </span>
                        )}
                        <div style={{ marginLeft: 'auto' }}>
                            <button
                                style={styles.applyBtn(selectedTeams.length === 0)}
                                disabled={selectedTeams.length === 0}
                                onClick={handleApplyFilter}
                            >
                                Apply Filter
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Round filter */}
            <div style={styles.filterToggle} onClick={() => setRoundFilterOpen(!roundFilterOpen)}>
                <span style={{ fontWeight: 'bold', fontSize: '14px', color: '#4b2e83' }}>
                    Filter Rounds {roundFilterOpen ? '\u25B2' : '\u25BC'}
                </span>
                <span style={styles.filterIndicator}>
                    {isRoundFilterActive
                        ? `Showing Round${selectedRounds.length > 1 ? 's' : ''} ${selectedRounds.join(', ')}`
                        : 'Showing all rounds'}
                </span>
            </div>

            {roundFilterOpen && (
                <div style={styles.filterPanel}>
                    <div style={{ display: 'flex', gap: '24px', marginBottom: '16px' }}>
                        {[1, 2, 3].map(r => (
                            <label key={r} style={styles.filterCheckbox}>
                                <input
                                    type="checkbox"
                                    checked={selectedRounds.includes(r)}
                                    onChange={() => toggleRound(r)}
                                />
                                Round {r}
                            </label>
                        ))}
                    </div>
                    <div style={styles.filterActions}>
                        {selectedRounds.length === 0 && (
                            <span style={{ color: '#e15759', fontSize: '13px' }}>
                                Select at least one round
                            </span>
                        )}
                        <div style={{ marginLeft: 'auto' }}>
                            <button
                                style={styles.applyBtn(selectedRounds.length === 0)}
                                disabled={selectedRounds.length === 0}
                                onClick={handleApplyRoundFilter}
                            >
                                Apply Filter
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Tab navigation */}
            <div style={styles.tabRow}>
                {TABS.map((tab, idx) => (
                    <button
                        key={tab.key}
                        style={{
                            ...styles.tab(activeTab === tab.key),
                            borderRadius: idx === 0 ? '8px 0 0 8px' : idx === TABS.length - 1 ? '0 8px 8px 0' : '0',
                        }}
                        onClick={() => setActiveTab(tab.key)}
                    >
                        {tab.label}
                    </button>
                ))}
            </div>

            {/* AI Insights */}
            <AiInsightsPanel
                gameId={gameId}
                cacheDocId="instructor"
                filterHash={insightFilterHash}
                buildSummary={buildGameSummary}
                insightType="instructor"
            />

            {/* Export button */}
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '16px' }}>
                <button
                    onClick={handleExportCsv}
                    style={{
                        padding: '8px 16px', backgroundColor: '#2e7d32', color: '#fff',
                        border: 'none', borderRadius: '6px', cursor: 'pointer',
                        fontSize: '13px', fontWeight: 'bold',
                    }}
                >
                    Export CSV
                </button>
            </div>

            {/* Active tab content */}
            {renderTab()}
        </div>
    );
};

export default ScoresPage;
